// AnalysedHere — what this deployment has already run on the thing in front of
// the analyst.
//
// One card, two identifiers. A digest searched on the submit page and a link
// pasted into the URL console raise the same question — have we done this
// already, and what did we find — and a reader deciding whether to spend a run
// reads the same four facts either way: the verdict it reached, what it was
// called, the family it was attributed to, and when.
//
// It lives apart from IntelSearchPanel because the URL console is not a search
// panel and must not import one to borrow a list.

import { ExternalLink, Loader2, Zap } from 'lucide-react';
import { Link } from 'react-router';
import type { IntelLocalAnalysis } from '@/lib/api';
import { cn } from '@/lib/classnames';
import { defang } from '@/lib/defang';
import { formatBytes } from '@/lib/format';

/** The verdict colours a LOCAL analysis earned. Unlike a feed record this is
 *  this platform's own observation, so it is stated in the verdict palette. */
const VERDICT_TONE: Record<string, string> = {
  malicious: 'border-verdict-malicious/40 text-verdict-malicious',
  suspicious: 'border-verdict-suspicious/40 text-verdict-suspicious',
  clean: 'border-verdict-clean/40 text-verdict-clean',
  inconclusive: 'border-ink-700 text-ink-300',
};

/**
 * The card: how many runs, and each of them as a row that opens its report.
 *
 * The count leads. "Analysed here" answers the question, and the number is what
 * says whether this was looked at once or is being re-run while something
 * changes — which is the difference between reading the old report and running
 * another analysis.
 *
 * title is for a surface where the question is not "have we" but "what else":
 * a report naming its target's other runs is answering a different question
 * with the same rows.
 *
 * rerun is offered wherever the target can be run from here. Knowing a file has
 * been analysed is half an answer; the other half is deciding to do it again,
 * and a card that reports without offering that sends the analyst back to the
 * upload they were trying to avoid.
 */
export function AnalysedHere({
  runs,
  title,
  rerun,
}: {
  runs: IntelLocalAnalysis[];
  title?: string;
  rerun?: { label: string; pending: boolean; onRerun: () => void };
}) {
  return (
    <div className="card" style={{ padding: '16px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '12px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontWeight: 500 }}>
          {title ?? 'Analysed here'} · {runs.length} {runs.length === 1 ? 'run' : 'runs'}
        </div>
        {rerun && (
          <button
            className="btn-secondary"
            style={{ marginLeft: 'auto' }}
            disabled={rerun.pending}
            onClick={rerun.onRerun}
            type="button"
          >
            {rerun.pending ? <Loader2 className="spin" size={14} /> : <Zap size={14} />}
            {rerun.label}
          </button>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {runs.map((run) => (
          <AnalysedHereRow key={run.submission_id} run={run} />
        ))}
      </div>
    </div>
  );
}

/** One prior run, as a link to its report. */
export function AnalysedHereRow({ run }: { run: IntelLocalAnalysis }) {
  const verdict = run.verdict ?? '';
  return (
    <Link
      className="ring-focus rounded-lg border border-ink-800 px-3 py-2.5 hover:border-ink-700 hover:bg-ink-850/60 transition-colors"
      style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}
      to={`/submissions/${run.submission_id}`}
    >
      {verdict ? (
        <span className={cn('pill', VERDICT_TONE[verdict] ?? 'border-ink-700 text-ink-300')}>
          {verdict}
          {/* > 0: a stored 0 is the pipeline's unset default (rows written
              before confidence went nullable), never a measured "0% sure". */}
          {typeof run.confidence === 'number' &&
            run.confidence > 0 &&
            ` · ${Math.round(run.confidence * 100)}%`}
        </span>
      ) : (
        // A run still in flight has no verdict YET, which is a different thing
        // from one that finished without producing one.
        <span className="pill border-ink-700 text-ink-400">{run.task_status ?? 'no verdict'}</span>
      )}
      <span className="wrap-anywhere" style={{ flex: 1, minWidth: '160px' }}>
        {/* Defanged when the row IS a link. The file lane's target is a
            filename and defanging one is noise; a URL is the thing the
            convention exists for — this card sits on the page an analyst is
            about to paste from, and the platform never shows a live one. */}
        {run.matched_via === 'url' ? defang(run.target_name) : run.target_name}
      </span>
      {run.family && <span className="text-muted text-sm">{run.family}</span>}
      {/* A URL submission has no bytes, and "0 B" beside a link reads as a
          transfer that failed rather than as a lane that has no size. */}
      {run.sample_size > 0 && (
        <span className="text-muted text-sm">{formatBytes(run.sample_size)}</span>
      )}
      <span className="text-muted text-sm">{new Date(run.submitted_at).toLocaleDateString()}</span>
      {run.matched_via === 'dropped' && (
        // The row means something different: this run did not analyse the file,
        // it produced it.
        <span className="pill border-ink-700 text-ink-400">dropped by this run</span>
      )}
      <ExternalLink aria-hidden className="text-ink-500" size={14} />
    </Link>
  );
}
